import { useEffect, useMemo, useState } from "react";
import {
  FiCheck,
  FiEdit3,
  FiImage,
  FiPackage,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTag,
  FiX,
} from "react-icons/fi";

import { adminApi } from "../../admin/api";
import { getAdminAuth } from "../../admin/adminStorage";

import "./AdminDashboard.css";
import { normalizeProductImages } from "../../data/loadProductById";

/* =========================================================
   HELPERS
========================================================= */

const moneyToNumber = (value) => {
  if (typeof value === "number") {
    return value;
  }

  return Number(String(value || "").replace(/[₹,\s]/g, "")) || 0;
};

const formatPrice = (value) => {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
};

const getCategory = (product) => {
  return product.displayCategory || product.category || "Other";
};

/* =========================================================
   NORMALISE API PRODUCT
========================================================= */

const normaliseApiProducts = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item, index) => {
    const images = normalizeProductImages(item);

    return {
      ...item,

      id: item.id ?? item.productId ?? index + 1,

      name: item.name ?? item.productName ?? "Unnamed Product",

      category: item.category ?? item.displayCategory ?? "Other",

      displayCategory: item.displayCategory ?? item.category ?? "Other",

      price: item.price ?? item.sellingPrice ?? 0,

      // image: item.image ?? item.imageUrl ?? item.images?.[0] ?? "",
      image: images[0] || "",
      images,
      soldOut:
        item.soldOut === true || String(item.soldOut).toLowerCase() === "true",
    };
  });
};

// Product name validation
const PRODUCT_NAME_REGEX = /^[a-zA-Z0-9\s&'()\-]+$/;

const validateProductName = (name) => {
  const trimmedName = name.trim();

  if (!trimmedName) {
    return "Please enter a product name.";
  }

  if (trimmedName.length < 2) {
    return "Product name must contain at least 2 characters.";
  }

  if (trimmedName.length > 100) {
    return "Product name cannot exceed 100 characters.";
  }

  if (!PRODUCT_NAME_REGEX.test(trimmedName)) {
    return "Product name contains invalid characters. Only letters, numbers, spaces, &, apostrophes, hyphens and parentheses are allowed.";
  }

  return "";
};
const emptyForm = {
  name: "",
  category: "",
  price: "",
  oldPrice: "",
  description: "",
  images: [],
  video: null,
  soldOut: false,
  isNewArrival: false,
  isSignature: false,
  isRare: false,
};
/* =========================================================
   COMPONENT
========================================================= */

export default function AdminDashboard() {
  const auth = getAdminAuth();

  /* =======================================================
     PRODUCTS
  ======================================================= */

  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [nameError, setNameError] = useState("");
  /* =======================================================
     CATEGORIES
  ======================================================= */

  const [backendCategories, setBackendCategories] = useState([]);

  const [loadingCategories, setLoadingCategories] = useState(false);

  /* =======================================================
     FILTER / SEARCH
  ======================================================= */

  const [activeCategory, setActiveCategory] = useState("All");

  const [search, setSearch] = useState("");

  /* =======================================================
     PRICE EDIT
  ======================================================= */

  const [editingId, setEditingId] = useState(null);

  const [priceDraft, setPriceDraft] = useState("");

  const [savingId, setSavingId] = useState(null);

  /* =======================================================
     GENERAL
  ======================================================= */

  const [message, setMessage] = useState("");

  const [previewImage, setPreviewImage] = useState(null);

  /* =======================================================
     ADD PRODUCT
  ======================================================= */

  const [showAddProduct, setShowAddProduct] = useState(false);

  // Product currently being edited. null means Add Product mode.
  const [editingProduct, setEditingProduct] = useState(null);

  const [form, setForm] = useState(emptyForm);

  const [creatingProduct, setCreatingProduct] = useState(false);

  /* =======================================================
     NEW CATEGORY
  ======================================================= */

  const [showNewCategory, setShowNewCategory] = useState(false);

  const [newCategory, setNewCategory] = useState("");

  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [pendingFormAction, setPendingFormAction] = useState(null);

  useEffect(() => {
    if (!message) return;

    const timer = setTimeout(() => {
      setMessage("");
    }, 4000);

    return () => clearTimeout(timer);
  }, [message]);

  /* =======================================================
     LOAD PRODUCTS
  ======================================================= */
  useEffect(() => {
    let mounted = true;

    const loadProducts = async () => {
      setLoading(true);

      try {
        const apiData = await adminApi.getProducts(auth?.token);
        console.log("Api data is ", apiData);
        const normalised = normaliseApiProducts(apiData);

        if (mounted) {
          setItems(
            normalised.map((product) => ({
              ...product,

              numericPrice: moneyToNumber(product.price),
            })),
          );
        }
      } catch (error) {
        if (mounted) {
          setMessage(error.message || "Unable to load products.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (auth?.token) {
      loadProducts();
    } else {
      setLoading(false);
      setMessage("Admin authentication token is missing.");
    }

    return () => {
      mounted = false;
    };
  }, [auth?.token]);

  /* =======================================================
     LOAD CATEGORIES FROM BACKEND
  ======================================================= */

  useEffect(() => {
    let mounted = true;

    const loadCategories = async () => {
      if (!auth?.token) {
        return;
      }

      setLoadingCategories(true);

      try {
        const data = await adminApi.getCategories(auth.token);

        if (!mounted) {
          return;
        }

        /*
          Backend may return:

          [
            {
              id: 1,
              name: "Paithani"
            }
          ]

          OR simply:

          [
            "Paithani",
            "Silk"
          ]
        */

        let normalisedCategories = [];

        if (Array.isArray(data)) {
          normalisedCategories = data
            .map((item) => {
              if (typeof item === "string") {
                return {
                  id: item,
                  name: item,
                };
              }

              return {
                id: item.id ?? item.categoryId ?? item.name,

                name: item.name ?? item.categoryName ?? item.title ?? "",
              };
            })
            .filter((item) => item.name.trim() !== "");
        }

        setBackendCategories(normalisedCategories);
      } catch (error) {
        if (mounted) {
          setMessage(error.message || "Unable to load categories.");
        }
      } finally {
        if (mounted) {
          setLoadingCategories(false);
        }
      }
    };

    loadCategories();

    return () => {
      mounted = false;
    };
  }, [auth?.token]);

  const requestSaveConfirmation = (event) => {
    event.preventDefault();

    setPendingFormAction(() =>
      editingProduct ? handleUpdateProduct : handleAddProduct,
    );

    setShowSaveConfirmation(true);
  };

  const confirmSaveProduct = () => {
    if (!pendingFormAction) return;

    setShowSaveConfirmation(false);

    // Run the original Add/Edit handler after confirmation.
    pendingFormAction({
      preventDefault: () => {},
    });

    setPendingFormAction(null);
  };

  const cancelSaveConfirmation = () => {
    setShowSaveConfirmation(false);
    setPendingFormAction(null);
  };
  /* =======================================================
     DASHBOARD CATEGORIES
  ======================================================= */

  const categories = useMemo(() => {
    const unique = [...new Set(items.map(getCategory).filter(Boolean))];

    return ["All", ...unique];
  }, [items]);

  /* =======================================================
     FILTERED PRODUCTS
  ======================================================= */

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();

    return items.filter((product) => {
      console.log("Products in table", product);
      const category = getCategory(product);

      const categoryMatch =
        activeCategory === "All" || category === activeCategory;

      const searchMatch =
        !query ||
        String(product.name || "")
          .toLowerCase()
          .includes(query) ||
        String(category || "")
          .toLowerCase()
          .includes(query) ||
        String(product.id).toLowerCase().includes(query);

      return categoryMatch && searchMatch;
    });
  }, [items, activeCategory, search]);

  /* =======================================================
     STATS
  ======================================================= */

  const stats = useMemo(() => {
    const categoryCount = new Set(items.map(getCategory)).size;
    // Treat 1, true, and "true" as sold out.
    const isSoldOut = (product) =>
      product.soldOut === 1 ||
      product.soldOut === true ||
      String(product.soldOut).toLowerCase() === "true";

    // Count active products (equivalent to SQL soldOut = 0).
    const activeProducts = items.filter(
      (product) => !isSoldOut(product),
    ).length;

    return [
      {
        label: "Active Products",
        value: activeProducts,
        icon: <FiPackage />,
        note: "Available products",
      },
      {
        label: "Categories",
        value: categoryCount,
        icon: <FiTag />,
        note: "Active collections",
      },
    ];
  }, [items]);
  /* =======================================================
     PRICE EDIT
  ======================================================= */

  const beginEdit = (product) => {
    setEditingId(product.id);

    setPriceDraft(String(product.numericPrice || ""));

    setMessage("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setPriceDraft("");
  };

  const savePrice = async (product) => {
    const nextPrice = Number(priceDraft);

    if (!Number.isFinite(nextPrice) || nextPrice <= 0) {
      setMessage("Please enter a valid price greater than ₹0.");

      return;
    }

    if (!auth?.token) {
      setMessage("Authentication token is missing. Please login again.");

      return;
    }

    setSavingId(product.id);
    setMessage("");

    try {
      await adminApi.updateProductPrice(product.id, nextPrice, auth.token);

      setItems((current) =>
        current.map((item) =>
          item.id === product.id
            ? {
                ...item,

                price: nextPrice,

                numericPrice: nextPrice,
              }
            : item,
        ),
      );

      setMessage(`Price updated for "${product.name}".`);

      cancelEdit();
    } catch (error) {
      setMessage(error.message || "Price update failed.");
    } finally {
      setSavingId(null);
    }
  };

  /* =======================================================
     FORM
  ======================================================= */

  const updateForm = (field, value) => {
    console.log("field", field, value);
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  /* =======================================================
     IMAGE FILES
  ======================================================= */

  const handleImageFiles = (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    const invalidFile = files.find((file) => !file.type.startsWith("image/"));

    if (invalidFile) {
      setMessage("Please select only valid image files.");

      return;
    }

    setForm((current) => ({
      ...current,

      images: [...current.images, ...files],
    }));
  };

  const removeImage = (index) => {
    setForm((current) => ({
      ...current,

      images: current.images.filter((_, imageIndex) => imageIndex !== index),
    }));
  };

  /* =======================================================
     CLOSE ADD PRODUCT
  ======================================================= */

  const closeAddProduct = () => {
    setShowAddProduct(false);
    setEditingProduct(null);

    setForm({
      ...emptyForm,
      images: [],
    });

    setShowNewCategory(false);
    setNewCategory("");
  };

  const openAddProduct = () => {
    setEditingProduct(null);
    setForm({
      ...emptyForm,
      images: [],
    });
    setShowNewCategory(false);
    setNewCategory("");
    setMessage("");
    setShowAddProduct(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);

    setForm({
      name: product.name || "",
      category: product.category || product.displayCategory || "",
      price: String(product.numericPrice || moneyToNumber(product.price)),

      oldPrice:
        product.oldPrice != null
          ? String(product.oldPrice)
          : product.OldPrice != null
            ? String(product.OldPrice)
            : "",

      description: product.description || "",
      images: [],

      // Existing video URL, if provided by the API
      video: null,

      soldOut: product.soldOut === true,
      isNewArrival: product.isNewArrival === true,
      isSignature: product.isSignature === true,
      isRare: product.isRare === true,
    });
    setShowNewCategory(false);
    setNewCategory("");
    setMessage("");
    setShowAddProduct(true);
  };

  /* =========================================================
   PRODUCT VIDEO
========================================================= */

  const handleVideoFile = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const allowedTypes = [
      "video/mp4",
      "video/webm",
      "video/ogg",
      "video/quicktime",
    ];

    if (!allowedTypes.includes(file.type)) {
      setMessage("Please select an MP4, WEBM, OGG or MOV video.");
      event.target.value = "";
      return;
    }

    // Limit video size to 100 MB
    if (file.size > 100 * 1024 * 1024) {
      setMessage("Video size must not exceed 100 MB.");
      event.target.value = "";
      return;
    }

    setMessage("");
    updateForm("video", file);
  };

  const removeVideo = () => {
    updateForm("video", null);
  };
  /* =======================================================
     UPDATE PRODUCT
  ======================================================= */

  const handleUpdateProduct = async (event) => {
    event.preventDefault();

    if (!editingProduct) {
      return;
    }

    const finalCategory = (
      showNewCategory ? newCategory : form.category
    ).trim();

    // const name = form.name.trim();

    // if (!name) {
    //   setMessage("Please enter a product name.");
    //   return;
    // }
    const name = form.name.trim();
    const error = validateProductName(name);

    if (error) {
      setNameError(error);
      return;
    }

    setNameError("");
    const price = Number(form.price);
    if (!finalCategory) {
      setMessage("Please select a category.");
      return;
    }

    if (!Number.isFinite(price) || price <= 0) {
      setMessage("Please enter a valid product price.");
      return;
    }

    if (!auth?.token) {
      setMessage("Authentication token is missing. Please login again.");
      return;
    }

    setCreatingProduct(true);
    setMessage("");

    const oldPrice =
      String(form.oldPrice ?? "").trim() === "" ? null : Number(form.oldPrice);

    if (oldPrice !== null && (!Number.isFinite(oldPrice) || oldPrice <= 0)) {
      setMessage(
        "Old Price must be greater than ₹0. Leave it blank to calculate automatically.",
      );
      return;
    }

    const finalOldPrice = oldPrice ?? Math.round(price / 0.9);
    try {
      const payload = {
        name,
        category: finalCategory,
        price,
        // oldPrice: form.oldPrice === "" ? null : Number(form.oldPrice),
        oldPrice: finalOldPrice,
        description: form.description.trim(),
        soldOut: form.soldOut,
        isNewArrival: form.isNewArrival,
        isSignature: form.isSignature,
        isRare: form.isRare,
        images: form.images,
        video: form.video,
      };
      const response = await adminApi.updateProduct(
        editingProduct.id,
        payload,
        auth.token,
      );

      const returnedProduct =
        response?.product || response?.data || response || {};

      setItems((current) =>
        current.map((item) =>
          String(item.id) === String(editingProduct.id)
            ? {
                ...item,
                ...returnedProduct,
                id:
                  returnedProduct?.id ?? returnedProduct?.productId ?? item.id,
                name,
                category: finalCategory,
                displayCategory:
                  returnedProduct?.displayCategory ??
                  returnedProduct?.category ??
                  finalCategory,
                price: returnedProduct?.price ?? price,
                numericPrice: moneyToNumber(returnedProduct?.price ?? price),
                description:
                  returnedProduct?.description ?? form.description.trim(),
                soldOut:
                  typeof returnedProduct?.soldOut === "boolean"
                    ? returnedProduct.soldOut
                    : form.soldOut,
                isNewArrival:
                  typeof returnedProduct?.isNewArrival === "boolean"
                    ? returnedProduct.isNewArrival
                    : form.isNewArrival,
                isSignature:
                  typeof returnedProduct?.isSignature === "boolean"
                    ? returnedProduct.isSignature
                    : form.isSignature,
                isRare:
                  typeof returnedProduct?.isRare === "boolean"
                    ? returnedProduct.isRare
                    : form.isRare,
              }
            : item,
        ),
      );

      setMessage(`"${name}" updated successfully.`);
      closeAddProduct();
    } catch (error) {
      setMessage(error.message || "Unable to update product.");
    } finally {
      setCreatingProduct(false);
    }
  };

  /* =======================================================
     ADD PRODUCT
  ======================================================= */

  const handleAddProduct = async (event) => {
    event.preventDefault();

    const finalCategory = (
      showNewCategory ? newCategory : form.category
    ).trim();

    // const name = form.name.trim();

    // if (!name) {
    //   setMessage("Please enter a product name.");

    //   return;
    // }
    const name = form.name.trim();
    const error = validateProductName(name);

    if (error) {
      setNameError(error);
      return;
    }

    setNameError("");
    const price = Number(form.price);
    if (!finalCategory) {
      setMessage("Please select a category.");

      return;
    }

    if (!Number.isFinite(price) || price <= 0) {
      setMessage("Please enter a valid product price.");

      return;
    }

    if (!auth?.token) {
      setMessage("Authentication token is missing. Please login again.");

      return;
    }

    setCreatingProduct(true);
    setMessage("");

    const oldPrice =
      String(form.oldPrice ?? "").trim() === "" ? null : Number(form.oldPrice);

    if (oldPrice !== null && (!Number.isFinite(oldPrice) || oldPrice <= 0)) {
      setMessage(
        "Old Price must be greater than ₹0. Leave it blank to calculate automatically.",
      );
      return;
    }

    const finalOldPrice = oldPrice ?? Math.round(price / 0.9);
    try {
      const payload = {
        name,
        category: finalCategory,
        price,
        // oldPrice: form.oldPrice === "" ? null : Number(form.oldPrice),
        oldPrice: finalOldPrice,
        description: form.description.trim(),
        soldOut: form.soldOut,
        isNewArrival: form.isNewArrival,
        isSignature: form.isSignature,
        isRare: form.isRare,
        images: form.images,
        video: form.video,
      };
      /*
        IMPORTANT:

        This now creates the product
        in your ASP.NET backend.

        It does NOT use:
        addFrontendProduct()
      */

      const createdProduct = await adminApi.createProduct(payload, auth.token);

      /*
        Backend may return the
        created product directly.
      */

      const returnedProduct =
        createdProduct?.product || createdProduct?.data || createdProduct;

      const nextItem = {
        ...returnedProduct,

        id: returnedProduct?.id ?? returnedProduct?.productId,

        name: returnedProduct?.name ?? name,

        category: returnedProduct?.category ?? finalCategory,

        displayCategory:
          returnedProduct?.displayCategory ??
          returnedProduct?.category ??
          finalCategory,

        price: returnedProduct?.price ?? price,

        numericPrice: moneyToNumber(returnedProduct?.price ?? price),

        image: returnedProduct?.image ?? returnedProduct?.imageUrl ?? "",

        soldOut: returnedProduct?.soldOut ?? form.soldOut,
      };

      /*
        Refresh the table from the API after creation.
        This keeps the dashboard row shape identical to the
        rows loaded when the dashboard first opens. The fallback
        still shows the created item if the follow-up GET fails.
      */
      try {
        const latestApiData = await adminApi.getProducts(auth.token);
        const latestProducts = normaliseApiProducts(latestApiData).map(
          (product) => ({
            ...product,
            numericPrice: moneyToNumber(product.price),
          }),
        );

        if (latestProducts.length > 0 || Array.isArray(latestApiData)) {
          setItems(latestProducts);
        } else {
          setItems((current) => [
            ...current.filter(
              (item) => String(item.id) !== String(nextItem.id),
            ),
            nextItem,
          ]);
        }
      } catch (refreshError) {
        console.warn(
          "Product was created, but refreshing the dashboard list failed:",
          refreshError,
        );
        setItems((current) => [
          ...current.filter((item) => String(item.id) !== String(nextItem.id)),
          nextItem,
        ]);
      }

      // Show the new row even if the user was viewing another category.
      setActiveCategory("All");
      setSearch("");

      setMessage(`"${name}" added successfully under ${finalCategory}.`);

      /*
        Refresh categories because
        backend may have returned/created
        a new category.
      */

      try {
        const categoryData = await adminApi.getCategories(auth.token);

        if (Array.isArray(categoryData)) {
          const normalised = categoryData
            .map((item) => {
              if (typeof item === "string") {
                return {
                  id: item,
                  name: item,
                };
              }

              return {
                id: item.id ?? item.categoryId ?? item.name,

                name: item.name ?? item.categoryName ?? item.title ?? "",
              };
            })
            .filter((item) => item.name.trim() !== "");

          setBackendCategories(normalised);
        }
      } catch {
        /*
          Product was already created.
          Category refresh failure should
          not show another error.
        */
      }

      closeAddProduct();
    } catch (error) {
      setMessage(error.message || "Unable to add product.");
    } finally {
      setCreatingProduct(false);
    }
  };

  /* =======================================================
     RETURN
  ======================================================= */

  return (
    <div className="admin-dashboard">
      {message && (
        <div className="admin-toast-container">
          <div
            className={`admin-toast ${
              /successfully|price updated/i.test(message)
                ? "admin-toast-success"
                : "admin-toast-error"
            }`}
            role="status"
            aria-live="polite"
          >
            <div className="admin-toast-icon">
              {/successfully|price updated/i.test(message) ? (
                <FiCheck />
              ) : (
                <span>!</span>
              )}
            </div>

            <div className="admin-toast-content">
              <strong>
                {/successfully|price updated/i.test(message)
                  ? "Success!"
                  : "Please check"}
              </strong>
              <p>{message}</p>
            </div>

            <button
              type="button"
              className="admin-toast-close"
              onClick={() => setMessage("")}
              aria-label="Close notification"
            >
              <FiX />
            </button>
          </div>
        </div>
      )}

      {/* ===================================================
          HEADING
      =================================================== */}

      <section className="admin-dashboard-heading">
        <div>
          <span className="admin-eyebrow">Overview</span>

          {/* <h1>Product Dashboard</h1>

          <p>
            View your collections category-wise, add new products and update
            individual product prices from one place.
          </p> */}
        </div>

        <div className="admin-heading-actions">
          <button className="admin-add-product-btn" onClick={openAddProduct}>
            <FiPlus />
            Add Product
          </button>

          <button
            className="admin-refresh-btn"
            onClick={() => window.location.reload()}
            title="Refresh dashboard"
          >
            <FiRefreshCw />
            Refresh
          </button>
        </div>
      </section>

      {/* ===================================================
          STATS
      =================================================== */}

      <section className="admin-stats-grid">
        {stats.map((stat) => (
          <article className="admin-stat-card" key={stat.label}>
            <div className="admin-stat-icon">{stat.icon}</div>

            <div>
              <p>{stat.label}</p>

              <strong>{stat.value}</strong>

              <span>{stat.note}</span>
            </div>
          </article>
        ))}
      </section>

      {/* ===================================================
          PRODUCT PANEL
      =================================================== */}

      <section className="admin-product-panel">
        <div className="admin-panel-head">
          <div>
            <h2>Product Catalogue</h2>

            <p>
              {filteredProducts.length} product
              {filteredProducts.length === 1 ? "" : "s"} displayed
            </p>
          </div>

          <div className="admin-search">
            <FiSearch />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search product or category..."
            />
          </div>
        </div>

        {/* =================================================
            CATEGORY FILTER
        ================================================= */}

        <div className="admin-category-scroll">
          {categories.map((category) => (
            <button
              key={category}
              className={activeCategory === category ? "active" : ""}
              onClick={() => setActiveCategory(category)}
            >
              {category}

              <span>
                {category === "All"
                  ? items.length
                  : items.filter((item) => getCategory(item) === category)
                      .length}
              </span>
            </button>
          ))}
        </div>

        {/* =================================================
            MESSAGE
        ================================================= */}

        {/* {message && (
          <div className="admin-action-message">
            <span>{message}</span>

            <button onClick={() => setMessage("")} aria-label="Close message">
              <FiX />
            </button>
          </div>
        )} */}

        {/* =================================================
            PRODUCTS
        ================================================= */}

        {loading ? (
          <div className="admin-loading">
            <div className="admin-spinner" />
            Loading products...
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="admin-empty">
            <FiPackage />

            <h3>No products found</h3>

            <p>Try another search or category.</p>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-products-table">
              <thead>
                <tr>
                  <th>Product</th>

                  <th>Category</th>

                  <th>Current Price</th>
                  <th></th>

                  {/* <th>Update Price</th> */}

                  <th>Edit Details</th>

                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {filteredProducts.map((product) => {
                  const isEditing = editingId === product.id;

                  const isSaving = savingId === product.id;

                  return (
                    <tr key={product.id}>
                      {/* PRODUCT */}

                      <td>
                        <div className="admin-product-cell">
                          <button
                            type="button"
                            className="admin-product-image"
                            onClick={() =>
                              product.image && setPreviewImage(product.image)
                            }
                            title="Click to view larger image"
                          >
                            {/* {product.image ? (
                              // <img src={product.image} alt={product.name} />
                              <img
                                src={product.image}
                                alt={product.name}
                                referrerPolicy="no-referrer"
                                loading="lazy"
                                onError={(e) => {
                                  console.error("Image failed:", product.image);
                                }}
                              />
                            ) : (
                              <FiPackage />
                            )} */}
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                loading="lazy"
                                onLoad={() => {
                                  console.log("Image loaded:", product.image);
                                }}
                                onError={(e) => {
                                  console.error("Image failed:", {
                                    product: product.name,
                                    imageUrl: product.image,
                                    actualUrl: e.currentTarget.src,
                                  });
                                }}
                              />
                            ) : (
                              <FiPackage />
                            )}
                          </button>

                          <div>
                            <strong>{product.name}</strong>

                            <small>ID: #{product.id}</small>
                          </div>
                        </div>
                      </td>

                      {/* CATEGORY */}

                      <td>
                        <span className="admin-category-pill">
                          {getCategory(product)}
                        </span>
                      </td>

                      {/* PRICE */}

                      <td>
                        <strong className="admin-current-price">
                          {formatPrice(product.numericPrice)}
                        </strong>
                      </td>

                      {/* UPDATE PRICE */}

                      <td>
                        {
                          isEditing ? (
                            <div className="admin-price-editor">
                              <div>
                                <span>₹</span>

                                <input
                                  autoFocus
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={priceDraft}
                                  onChange={(event) =>
                                    setPriceDraft(event.target.value)
                                  }
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                      savePrice(product);
                                    }

                                    if (event.key === "Escape") {
                                      cancelEdit();
                                    }
                                  }}
                                />
                              </div>

                              <button
                                className="save"
                                onClick={() => savePrice(product)}
                                disabled={isSaving}
                                title="Save price"
                              >
                                <FiCheck />
                              </button>

                              <button
                                className="cancel"
                                onClick={cancelEdit}
                                disabled={isSaving}
                                title="Cancel"
                              >
                                <FiX />
                              </button>
                            </div>
                          ) : null
                          // (
                          //   <button
                          //     className="admin-edit-price"
                          //     onClick={() => beginEdit(product)}
                          //   >
                          //     <FiEdit3 />
                          //     Edit Price
                          //   </button>
                          // )
                        }
                      </td>

                      {/* EDIT DETAILS */}

                      <td>
                        <button
                          type="button"
                          className="admin-edit-price admin-edit-details"
                          onClick={() => openEditProduct(product)}
                        >
                          <FiEdit3 />
                          Edit Product
                        </button>
                      </td>

                      {/* STATUS */}

                      <td>
                        {product.soldOut === true ? (
                          <span className="admin-active-status sold-out">
                            <i />
                            Sold Out
                          </span>
                        ) : (
                          <span className="admin-active-status active">
                            <i />
                            Active
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ===================================================
          API STATUS
      =================================================== */}

      {/* <p className="admin-api-hint">
        Product data and categories are now loaded from the{" "}
        <strong>.NET Core API</strong>.
      </p> */}

      {/* ===================================================
          ADD PRODUCT MODAL
      =================================================== */}

      {showAddProduct && (
        <div
          className="admin-product-modal"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeAddProduct();
            }
          }}
        >
          {/* <form
            className="admin-product-form"
            onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct}
          > */}
          <form
            className="admin-product-form"
            onSubmit={requestSaveConfirmation}
          >
            {/* HEADER */}

            <div className="admin-product-form-head">
              <div>
                <span className="admin-eyebrow">Catalogue</span>

                <h2>{editingProduct ? "Edit Product" : "Add Product"}</h2>

                <p>
                  {editingProduct
                    ? "Update price, description and product availability."
                    : "Add a product directly to your backend catalogue."}
                </p>
              </div>

              <button
                type="button"
                className="admin-form-close"
                onClick={closeAddProduct}
                aria-label="Close add product form"
              >
                <FiX />
              </button>
            </div>

            {/* FORM GRID */}

            <div className="admin-form-grid">
              {/* PRODUCT NAME */}

              {/* <label>
                <span>Product Name *</span>

                <input
                  type="text"
                  value={form.name}
                  onChange={(event) => updateForm("name", event.target.value)}
                  placeholder="e.g. Pure Silk Paithani Saree"
                  required
                />
              </label> */}

              <label className="product-name-field">
                <span>Product Name *</span>

                <input
                  type="text"
                  value={form.name}
                  maxLength={100}
                  onChange={(event) => {
                    const value = event.target.value;
                    updateForm("name", value);

                    const error = validateProductName(value);
                    setNameError(error);
                  }}
                  onBlur={() => {
                    setNameError(validateProductName(form.name));
                  }}
                  placeholder="e.g. Pure Silk Paithani Saree"
                  required
                />

                {nameError && (
                  <small className="field-error">{nameError}</small>
                )}
              </label>

              {/* PRICE */}

              <label>
                <span>Price *</span>

                <div className="admin-form-price">
                  <b>₹</b>

                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.price}
                    onChange={(event) =>
                      updateForm("price", event.target.value)
                    }
                    placeholder="4500"
                    required
                  />
                </div>
              </label>

              {/* OLD PRICE */}

              <label>
                <span>Old Price</span>

                <div className="admin-form-price">
                  <b>₹</b>

                  {/* <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.oldPrice}
                    onChange={(event) =>
                      updateForm("oldPrice", event.target.value)
                    }
                    placeholder="6500"
                  /> */}

                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.oldPrice}
                    onChange={(event) =>
                      updateForm("oldPrice", event.target.value)
                    }
                    placeholder="Leave blank for automatic calculation"
                  />
                </div>

                {/* <small>Optional: Original price before discount.</small> */}
              </label>

              {/* CATEGORY */}

              <label className="admin-category-field">
                <span>Category *</span>

                {!showNewCategory ? (
                  <div className="admin-category-select-row">
                    <select
                      value={form.category}
                      onChange={(event) =>
                        updateForm("category", event.target.value)
                      }
                      required
                    >
                      <option value="">
                        {loadingCategories
                          ? "Loading categories..."
                          : "Select category"}
                      </option>

                      {form.category &&
                        !backendCategories.some(
                          (category) => category.name === form.category,
                        ) && (
                          <option value={form.category}>{form.category}</option>
                        )}

                      {backendCategories.map((category) => (
                        <option key={category.id} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCategory(true);

                        updateForm("category", "");
                      }}
                    >
                      <FiPlus />
                      New
                    </button>
                  </div>
                ) : (
                  <div className="admin-category-select-row">
                    <input
                      type="text"
                      value={newCategory}
                      onChange={(event) => setNewCategory(event.target.value)}
                      placeholder="e.g. Banarasi Silk"
                      autoFocus
                      required
                    />

                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCategory(false);

                        setNewCategory("");
                      }}
                    >
                      Existing
                    </button>
                  </div>
                )}
              </label>

              {/* DESCRIPTION */}

              <label className="admin-form-full">
                <span>Description</span>

                <textarea
                  rows="3"
                  value={form.description}
                  onChange={(event) =>
                    updateForm("description", event.target.value)
                  }
                  placeholder="Short product description..."
                />
              </label>

              {/* IMAGES */}

              {/* <label className="admin-form-full">
                <span>Product Images</span>

                <div className="admin-image-input">
                  <div className="admin-image-input-preview">
                    {form.images.length > 0 ? (
                      <img
                        src={URL.createObjectURL(form.images[0])}
                        alt="Product preview"
                      />
                    ) : editingProduct?.image ? (
                      <img
                        src={editingProduct.image}
                        alt={editingProduct.name || "Current product"}
                      />
                    ) : (
                      <FiImage />
                    )}
                  </div>

                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageFiles}
                    />

                    <small>Select one or multiple product images.</small>

                    {form.images.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                          marginTop: "10px",
                        }}
                      >
                        {form.images.map((file, index) => (
                          <div
                            key={`${file.name}-${index}`}
                            style={{
                              position: "relative",
                            }}
                          >
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Product ${index + 1}`}
                              style={{
                                width: "65px",
                                height: "65px",
                                objectFit: "cover",
                                borderRadius: "8px",
                              }}
                            />

                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              style={{
                                position: "absolute",
                                top: "-6px",
                                right: "-6px",
                                width: "20px",
                                height: "20px",
                                border: "none",
                                borderRadius: "50%",
                                cursor: "pointer",
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </label> */}

              {/* PRODUCT VIDEO */}

              {/* <label className="admin-form-full">
                <span>Product Video</span>

                <div className="admin-video-upload">
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/ogg,video/quicktime"
                    onChange={handleVideoFile}
                  />

                  <small>
                    Upload an MP4, WEBM, OGG or MOV video. Maximum size: 100 MB.
                  </small>

                  {form.video && (
                    <div className="admin-video-preview">
                      <video
                        src={URL.createObjectURL(form.video)}
                        controls
                        preload="metadata"
                        style={{
                          width: "100%",
                          maxWidth: "360px",
                          maxHeight: "220px",
                          borderRadius: "8px",
                          marginTop: "12px",
                        }}
                      />

                      <p>{form.video.name}</p>

                      <button
                        type="button"
                        className="secondary"
                        onClick={removeVideo}
                      >
                        <FiX />
                        Remove Video
                      </button>
                    </div>
                  )}

                  {!form.video && editingProduct?.videoUrl && (
                    <div className="admin-video-preview">
                      <video
                        src={editingProduct.videoUrl}
                        controls
                        preload="metadata"
                        style={{
                          width: "100%",
                          maxWidth: "360px",
                          maxHeight: "220px",
                          borderRadius: "8px",
                          marginTop: "12px",
                        }}
                      />

                      <small>Current product video</small>
                    </div>
                  )}
                </div>
              </label>

              {/* SOLD OUT */}

              <label className="admin-soldout-check">
                <input
                  type="checkbox"
                  checked={form.soldOut}
                  onChange={(event) =>
                    updateForm("soldOut", event.target.checked)
                  }
                />

                <span>Mark product as Sold Out</span>
              </label>

              {/* NEW ARRIVAL */}

              <label className="admin-soldout-check">
                <input
                  type="checkbox"
                  checked={form.isNewArrival}
                  onChange={(event) =>
                    updateForm("isNewArrival", event.target.checked)
                  }
                />

                <span>Mark as New Arrival</span>
              </label>

              {/* SIGNATURE */}

              {/* <label className="admin-soldout-check">
                <input
                  type="checkbox"
                  checked={form.isSignature}
                  onChange={(event) =>
                    updateForm("isSignature", event.target.checked)
                  }
                />

                <span>Mark as Signature</span>
              </label> */}

              {/* RARE */}

              {/* <label className="admin-soldout-check">
                <input
                  type="checkbox"
                  checked={form.isRare}
                  onChange={(event) =>
                    updateForm("isRare", event.target.checked)
                  }
                />

                <span>Mark as Rare</span>
              </label> */}
            </div>

            {/* ACTIONS */}

            <div className="admin-product-form-actions">
              <button
                type="button"
                className="secondary"
                onClick={closeAddProduct}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="primary"
                disabled={creatingProduct}
              >
                {editingProduct ? <FiCheck /> : <FiPlus />}

                {creatingProduct
                  ? editingProduct
                    ? "Saving..."
                    : "Adding..."
                  : editingProduct
                    ? "Save Changes"
                    : "Add Product"}
              </button>
            </div>
          </form>
        </div>
      )}

      {showSaveConfirmation && (
        <div className="admin-confirm-overlay">
          <div
            className="admin-confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
          >
            <div className="admin-confirm-icon">
              <FiCheck />
            </div>

            <h3 id="confirm-title">
              {editingProduct
                ? "Confirm Product Update"
                : "Confirm New Product"}
            </h3>

            <p>
              Are you sure you want to{" "}
              {editingProduct ? "save changes to" : "add"}{" "}
              <strong>{form.name.trim() || "this product"}</strong>?
            </p>

            <div className="admin-confirm-actions">
              <button
                type="button"
                className="secondary"
                onClick={cancelSaveConfirmation}
                disabled={creatingProduct}
              >
                Cancel
              </button>

              <button
                type="button"
                className="primary"
                onClick={confirmSaveProduct}
                disabled={creatingProduct}
              >
                {creatingProduct
                  ? "Saving..."
                  : editingProduct
                    ? "Yes, Update"
                    : "Yes, Add Product"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ===================================================
          IMAGE PREVIEW
      =================================================== */}

      {previewImage && (
        <div
          className="admin-image-modal"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="admin-image-modal-content"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="admin-image-modal-close"
              onClick={() => setPreviewImage(null)}
              aria-label="Close image preview"
            >
              <FiX />
            </button>

            <img
              src={previewImage}
              alt="Product preview"
              className="admin-large-product-image"
            />
          </div>
        </div>
      )}
    </div>
  );
}
